// Implements: F-UIX-019, F-GEN-022, F-GEN-027
/**
 * Mockup v3 P9 Modelos "Flujos": the generation flows with their current revision and step roles
 * (read-only), read through `GET /flows`. Loading, error (with its retry) and empty states are
 * the tab's own (F-UIX-010).
 */

import { useQuery } from "@tanstack/react-query";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { FLOWS_KEY } from "../../../shared/api/queryKeys";
import { useTranslation } from "../../../shared/i18n";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { TableSkeleton } from "../../../shared/ui/molecules/TableSkeleton";
import type { FlowSummaryView } from "../../../types/reports";
import { fetchFlows } from "../api/flowsApi";
import { FlowsTable } from "../components/FlowsTable";

export function FlowsContainer(): ReactElement {
  const { t } = useTranslation();
  const flows = useQuery<readonly FlowSummaryView[], ApiError>({
    queryKey: FLOWS_KEY,
    queryFn: ({ signal }) => fetchFlows(signal),
  });
  if (flows.isError) {
    return (
      <ErrorState
        headingLevel={2}
        error={asApiError(flows.error)}
        onRetry={() => {
          void flows.refetch();
        }}
      />
    );
  }
  if (flows.data === undefined) return <TableSkeleton />;
  if (flows.data.length === 0) {
    return <EmptyState icon="chart" headingLevel={2} title={t("admin.flows.empty.title")} body={t("admin.flows.empty.body")} />;
  }
  return <FlowsTable flows={flows.data} />;
}
